import { apiClient } from '../lib/apiClient';

/**
 * Opt-in prompts, e.g. the Instagram campaign consents (backend
 * src/routes/meOptInPrompts.routes.ts, src/routes/adminOptInPrompts.routes.ts,
 * definitions in src/config/optInPrompts.ts):
 *   GET  /api/v1/me/opt-in-prompts                         → { data: MyOptInPrompt[] }
 *   POST /api/v1/me/opt-in-prompts/:id/responses { answers, source, locale } → { data: OptInResponse }
 *   POST /api/v1/me/opt-in-prompts/:id/shown               → 204
 *   GET  /api/v1/admin/opt-in-prompts                      → { data: AdminOptInPrompt[] }
 *   PUT  /api/v1/admin/opt-in-prompts/:id { active }       → { data: { active } }
 *   GET  /api/v1/admin/opt-in-prompts/:id/responses?limit&offset&granted
 *
 * Texts: locale keys optInPrompts.<id>.* (title, intro, fields.<key>.label/hint).
 */
export type OptInAnswers = Record<string, boolean | string | null>;
export type OptInResponseSource = 'prompt' | 'settings';

export type OptInField =
  | { type: 'consent'; key: string }
  | { type: 'text'; key: string; maxLength: number; requiredWhen: string[]; onlyWith: string[] };

export interface OptInResponse {
  version: number;
  answers: OptInAnswers;
  granted: boolean;
  createdAt: string;
}

export interface MyOptInPrompt {
  id: string;
  version: number;
  active: boolean;
  fields: OptInField[];
  due: boolean;
  response: OptInResponse | null;
}

export interface AdminOptInPrompt {
  id: string;
  version: number;
  active: boolean;
  fields: OptInField[];
  granted: number;
  declined: number;
  consents: Record<string, number>;
}

export interface AdminOptInResponse extends OptInResponse {
  userId: string;
  userEmail: string | null;
  userName: string | null;
  source: OptInResponseSource;
  locale: string | null;
}

export const MY_OPT_IN_PROMPTS_QUERY_KEY = ['me', 'opt-in-prompts'] as const;

/** "de-DE" → "de"; anything the API would refuse is left out. */
function toLocale(value: string | undefined): string | undefined {
  const base = value?.split(/[-_]/)[0]?.toLowerCase();
  return base && /^[a-z]{2,3}$/.test(base) ? base : undefined;
}

export async function fetchMyOptInPrompts(): Promise<MyOptInPrompt[]> {
  const body = await apiClient.get<{ data?: MyOptInPrompt[] }>('/api/v1/me/opt-in-prompts');
  return Array.isArray(body?.data) ? body.data : [];
}

export async function submitOptInResponse(
  promptId: string,
  answers: OptInAnswers,
  source: OptInResponseSource,
  locale?: string,
): Promise<OptInResponse> {
  const body = await apiClient.post<{ data: OptInResponse }>(
    `/api/v1/me/opt-in-prompts/${encodeURIComponent(promptId)}/responses`,
    { answers, source, locale: toLocale(locale) },
  );
  return body.data;
}

export async function markOptInPromptShown(promptId: string): Promise<void> {
  await apiClient.post(`/api/v1/me/opt-in-prompts/${encodeURIComponent(promptId)}/shown`, {});
}

export async function fetchAdminOptInPrompts(): Promise<AdminOptInPrompt[]> {
  const body = await apiClient.get<{ data?: AdminOptInPrompt[] }>('/api/v1/admin/opt-in-prompts');
  return body?.data ?? [];
}

export async function setAdminOptInPromptActive(promptId: string, active: boolean): Promise<boolean> {
  const body = await apiClient.put<{ data?: { active?: boolean } }>(
    `/api/v1/admin/opt-in-prompts/${encodeURIComponent(promptId)}`,
    { active },
  );
  return typeof body?.data?.active === 'boolean' ? body.data.active : active;
}

export async function fetchAdminOptInResponses(
  promptId: string,
  options: { limit: number; offset: number; grantedOnly: boolean },
): Promise<{ items: AdminOptInResponse[]; total: number }> {
  const res = await apiClient.get<{ data?: AdminOptInResponse[]; meta?: { total?: number } }>(
    `/api/v1/admin/opt-in-prompts/${encodeURIComponent(promptId)}/responses?limit=${options.limit}&offset=${options.offset}&granted=${options.grantedOnly}`,
  );
  return { items: res?.data ?? [], total: res?.meta?.total ?? 0 };
}
